/**
 * Telemetry helpers for the trading mutation hooks — relocated from
 * frontend/src/queries/transactions.ts:155-294.
 *
 * Pure over the deps slice (`emitEvent`, `trackAction`, `getActiveNetwork`,
 * `readSellDeeplinkEntryPoint`). SDK never imports Statsig or Better Stack.
 */

import { hashDiagnosticEntity } from '../../../../tx-engine/diagnostics/lifecycle.js';
import {
  lookupErrorMessageKey,
  safeRecoveryActionType,
  safeTxErrorCode,
  safeTxStage,
  TxErrorCode,
  type TxErrorCodeType,
} from '../../../../tx-engine/errors/errors.js';
import type { TxErrorInfo } from '../../../../tx-engine/index.js';
import type { TradeContext, TradeMutationDeps } from '../types.js';

type TelemetryDeps = Pick<
  TradeMutationDeps,
  'emitEvent' | 'getActiveNetwork' | 'reportError' | 'sellDeeplink'
>;

export function trackTradeAttempted(deps: TelemetryDeps, ctx: TradeContext): void {
  const entryPoint = deps.sellDeeplink?.readEntryPoint();
  deps.emitEvent('trade_attempted', {
    traceId: ctx.traceId,
    instruction: ctx.instruction,
    walletType: ctx.walletType,
    walletAddress: ctx.walletAddress,
    network: deps.getActiveNetwork(),
    marketAddress: ctx.marketAddress,
    outcomeIndex: ctx.outcomeIndex,
    ...(ctx.platformFeeD6 === undefined ? {} : { platformFeeD6: ctx.platformFeeD6 }),
    ...(ctx.tradeNotionalD6 === undefined ? {} : { tradeNotionalD6: ctx.tradeNotionalD6 }),
    ...(entryPoint !== undefined ? { entryPoint } : {}),
  });
}

export function trackTxSuccess(deps: TelemetryDeps, ctx: TradeContext, signature: string): void {
  const network = deps.getActiveNetwork();
  // Spec 083 / SC-007: deep-link entry-point tag for trade-success analytics.
  const entryPoint = deps.sellDeeplink?.readEntryPoint();
  deps.emitEvent(
    ['buy', 'sell', 'swap'].includes(ctx.instruction)
      ? 'trade_success'
      : `${ctx.instruction}_success`,
    {
      traceId: ctx.traceId,
      instruction: ctx.instruction,
      signature,
      walletType: ctx.walletType,
      walletAddress: ctx.walletAddress,
      latencyMs: ctx.latencyMs,
      network,
      marketAddress: ctx.marketAddress,
      outcomeIndex: ctx.outcomeIndex,
      ...(entryPoint !== undefined ? { entryPoint } : {}),
      // Revenue attribution (LTV): quote-time estimates threaded by the host.
      // The D6 string is exact; the Usd number exists because downstream
      // analytics (Customer.io / Statsig) can only sum numeric properties.
      ...(ctx.tradeNotionalD6 !== undefined
        ? {
            tradeNotionalD6: ctx.tradeNotionalD6,
            tradeNotionalUsd: d6ToUsdNumber(ctx.tradeNotionalD6),
          }
        : {}),
      ...(ctx.platformFeeD6 !== undefined
        ? { platformFeeD6: ctx.platformFeeD6, platformFeeUsd: d6ToUsdNumber(ctx.platformFeeD6) }
        : {}),
    },
  );
}

/**
 * D6 string → USD number for analytics aggregation ONLY. Trade notionals and
 * fees sit far below 2^53 so the conversion is lossless at analytics
 * precision; this value never feeds money-path math (numeric-precision rule:
 * floats are display/reporting-edge only).
 */
function d6ToUsdNumber(d6: string): number {
  return Number(d6) / 1_000_000;
}

export function trackTxFailure(deps: TelemetryDeps, ctx: TradeContext, error: TxErrorInfo): void {
  const network = deps.getActiveNetwork();
  const morfiError = safeMorfiError(error.morfiError);
  deps.emitEvent('trade_error', {
    traceId: ctx.traceId,
    instruction: ctx.instruction,
    walletType: ctx.walletType,
    walletAddress: ctx.walletAddress,
    errorCode: safeTxErrorCode(error.code),
    stage: safeTxStage(error.stage),
    latencyMs: ctx.latencyMs,
    network,
    marketAddress: ctx.marketAddress,
    outcomeIndex: ctx.outcomeIndex,
    morfiError,
    recoveryAction: safeRecoveryActionType(error.recoveryAction.type),
    errorClass: 'TxError',
  });
}

export function trackTxException(
  deps: TelemetryDeps,
  ctx: TradeContext,
  error: unknown,
  errorCode: TxErrorCodeType = TxErrorCode.TX_UNKNOWN,
): void {
  const network = deps.getActiveNetwork();
  const errorClass = error instanceof Error ? safeErrorClass(error.name) : typeof error;
  deps.emitEvent('trade_error', {
    traceId: ctx.traceId,
    instruction: ctx.instruction,
    walletType: ctx.walletType,
    walletAddress: ctx.walletAddress,
    errorCode,
    stage: 'preflight',
    latencyMs: ctx.latencyMs,
    network,
    marketAddress: ctx.marketAddress,
    outcomeIndex: ctx.outcomeIndex,
    morfiError: 'none',
    recoveryAction: 'prompt_retry',
    errorClass,
  });
  // The report is a synthetic marker and the thrown message stays out of it
  // (provider text can carry customer data), so the transport status is the
  // one bounded fact that tells a rate-limited build from any other failure.
  const httpStatus = readHttpStatus(error);
  deps.reportError(new Error(`tx_${ctx.instruction}_exception`), {
    source: 'tx-engine',
    traceId: ctx.traceId,
    instruction: ctx.instruction,
    errorCode,
    errorClass,
    ...(httpStatus === undefined ? {} : { httpStatus }),
    walletType: ctx.walletType,
    walletHash: hashDiagnosticEntity(ctx.walletAddress),
    marketHash: hashDiagnosticEntity(ctx.marketAddress),
    network,
  });
}

/** RPC transport errors carry `httpStatus` (e.g. 429); everything else does not. */
function readHttpStatus(error: unknown): number | undefined {
  if (!(error instanceof Error) || !('httpStatus' in error)) return undefined;
  return typeof error.httpStatus === 'number' ? error.httpStatus : undefined;
}

function safeErrorClass(value: string): string {
  return [
    'AbortError',
    'Error',
    'RangeError',
    'ReferenceError',
    'SyntaxError',
    'TypeError',
  ].includes(value)
    ? value
    : 'Error';
}

function safeMorfiError(value: string | undefined): string {
  return value !== undefined && lookupErrorMessageKey(value) !== undefined ? value : 'none';
}
